import React, { useEffect, useRef, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { Add, HighlightOffRounded } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { MenuItem, Select, TextField } from '@material-ui/core';
import { BootstrapInput } from '../custom-components/CustomButtons';
import Button from '@material-ui/core/Button';
import { respondToReview } from '../../models/api/documents';
import { useDispatch, useSelector } from 'react-redux';
import { getDocumentState } from '../../features/document/selectors';
import {
  DocumentTemplateType,
  FileCategoryType,
  RespondToReviewPropsResponseEnum,
} from '../../api-client/autogenerated';
import { updateDocument } from '../../features/document/actions';
import { useHistory } from 'react-router-dom';
import {
  documentTypeToReadable,
  documentTypeToUrl,
  isSimpleWorkflowDoc,
  waitForFileToBeVerifiedByUrl,
} from '../../scripts/utils';
import { getDocumentsType } from '../../features/documents/selectors';
import { copyGeneralDocumentFileFromStaging, uploadToStaging } from '../../models/api/filesystem';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import FileUploadDialog from '../dialogs/FileUploadDialog';
import CircularLoader from '../loader/CircularLoader';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import PDFIcon from '../icons/PDF-icon';

type Props = {
  handleClose: () => void;
  setMaxHeight?: React.Dispatch<React.SetStateAction<number>>;
  documentTitle?: string;
};

const useStyles = makeStyles((theme) => ({
  container: {
    background: '#FFFFFF',
    borderRadius: '4px',
    padding: '16px',
    width: '400px',
    zIndex: 1,
    minWidth: '400px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: '100%',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: '8px',
    marginBottom: '16px',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '21px',
    textAlign: 'left',
    color: '#0947B9',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: 16,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  submitButton: {
    height: '32px',
    background: '#0947B9',
    color: '#FFF',
    borderRadius: '4px',
    '&:hover': {
      background: '#4380ED',
    },
  },
}));

export default function Complete(props: Props) {
  const classes = useStyles();
  const { handleClose, setMaxHeight, documentTitle } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const docType = useSelector(getDocumentsType)!;
  const document = useSelector(getDocumentState)!;

  const [acceptance, setAcceptance] = useState<RespondToReviewPropsResponseEnum>();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');

  const [description, setDescription] = useState<string>();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [stagingFileKeys, setStagingFileKeys] = useState<{ file: File; key: string }[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const ref = useRef<any>();
  useEffect(() => {
    if (ref.current && setMaxHeight) setMaxHeight(ref.current.clientHeight);
  });

  const handleUploadProgress = (event: any) => {
    setUploadProgress(Math.round((100 * event.loaded) / event.total));
  };

  const stagingUpload = (file: File) => {
    setUploadMessage(`Uploading ${file.name}...`);
    return uploadToStaging(
      { fileName: file.name, useMultiPartUpload: file.size > MULTI_PART_FILE_SIZE },
      file,
      handleUploadProgress,
    );
  };

  const uploadFiles = async () => {
    try {
      dispatch(blockNavigation());

      const stagingKeys: { file: File; key: string }[] = [];
      for (let i = 0; i < uploadedFiles.length; i += 1) {
        const file = uploadedFiles[i];

        const { s3Key } = await stagingUpload(file);
        stagingKeys.push({ file, key: s3Key });

        setUploadProgress(0);
      }

      setUploadMessage('Verifying uploads...');
      await Promise.all(stagingKeys.map(async ({ key }) => waitForFileToBeVerifiedByUrl(key)));

      setStagingFileKeys(stagingKeys);
    } finally {
      dispatch(allowNavigation());
      setUploadDialogOpen(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    dispatch(blockNavigation());

    try {
      if (acceptance === RespondToReviewPropsResponseEnum.Resolved) {
        await handleResolve();
      } else {
        await handleResubmit();
      }
    } finally {
      dispatch(allowNavigation());
      setIsSubmitting(false);
    }
  };

  const handleResolve = async () => {
    const { document: updatedDocument } = await respondToReview(document.id, {
      response: RespondToReviewPropsResponseEnum.Resolved,
    });
    dispatch(updateDocument(updatedDocument));
  };

  const handleResubmit = async () => {
    try {
      const { revisedDocument } = await respondToReview(document.id, {
        response: RespondToReviewPropsResponseEnum.Resubmitted,
        newDescription: description,
      });

      if (!revisedDocument) {
        return;
      }

      for (let i = 0; i < stagingFileKeys.length; i += 1) {
        const { file, key } = stagingFileKeys[i];

        await copyGeneralDocumentFileFromStaging(
          revisedDocument.id,
          key,
          isSimpleWorkflowDoc(docType)
            ? FileCategoryType.DocumentGeneralContractorApproved
            : FileCategoryType.DocumentAttachments,
          file.name,
        );

        setUploadProgress(0);
      }

      dispatch(allowNavigation());
      history.push(
        `/main/projects/${document.projectId}/documents/${documentTypeToUrl[docType]}${
          revisedDocument ? `/${revisedDocument.id}` : ''
        }`,
      );
    } catch {}
  };

  const getUploadDialogTitle = () => {
    if (acceptance === RespondToReviewPropsResponseEnum.Resubmitted) return 'Upload Files';

    if (docType === DocumentTemplateType.PayApplications) return 'Upload Final Executed';

    return 'Upload Final Deliverable';
  };

  const handleAddFiles = (files: File[]) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (fileToDelete: File) => {
    setUploadedFiles((prev) => prev.filter((f) => f !== fileToDelete));
    setStagingFileKeys((prev) => prev.filter(({ file }) => file !== fileToDelete));
  };

  const getUploadDisabled = () => {
    if (isSubmitting) return true;
    if (!acceptance) return true;

    if (
      docType === DocumentTemplateType.PotentialChangeOrders &&
      acceptance === RespondToReviewPropsResponseEnum.Resubmitted
    ) {
      return !stagingFileKeys.length && !description;
    }

    return false;
  };

  const getContent = () => {
    if (acceptance === RespondToReviewPropsResponseEnum.Resubmitted) {
      return (
        <>
          <TextField
            variant="outlined"
            label={`${documentTypeToReadable[docType]} #`}
            fullWidth
            disabled
            defaultValue={`${document.referenceNumber}-R${(document.revisionNumber || 0) + 1}`}
            style={{ marginBottom: 12 }}
          />
          <TextField
            variant="outlined"
            label="Description"
            fullWidth
            multiline
            rows={5}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setUploadDialogOpen(true)}
            style={{ marginTop: '12px', marginBottom: '12px' }}
          >
            Upload Files
          </Button>
          {stagingFileKeys.map(({ file }) => (
            <div
              key={file.name + file.lastModified}
              style={{ display: 'flex', marginTop: '8px', alignItems: 'center' }}
            >
              <IconButton
                onClick={() => handleRemoveFile(file)}
                style={{ padding: 0, marginRight: '6px' }}
              >
                <HighlightOffRounded />
              </IconButton>
              <PDFIcon style={{ marginRight: '6px' }} />
              <span>{file.name}</span>
            </div>
          ))}
        </>
      );
    }
  };

  return (
    <div className={classes.container} ref={ref}>
      <div className={classes.titleContainer}>
        <Typography className={classes.title}>
          {documentTitle ? `Complete ${documentTitle}` : 'Complete'}
        </Typography>
        <div style={{ display: 'inline-flex', flexGrow: 100 }} />
        <IconButton
          onClick={handleClose}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </div>
      <div className={classes.content}>
        <Select
          variant="outlined"
          displayEmpty
          value={acceptance}
          onChange={(event) =>
            setAcceptance(event.target.value as RespondToReviewPropsResponseEnum)
          }
          input={<BootstrapInput />}
          style={{ margin: '16px 0px', borderRadius: 4 }}
        >
          <MenuItem value={undefined} disabled>
            {docType === DocumentTemplateType.Task || docType === DocumentTemplateType.CaTask
              ? 'Assigner'
              : 'Contractor'}{' '}
            Acceptance
          </MenuItem>
          <MenuItem value={RespondToReviewPropsResponseEnum.Resubmitted}>
            {docType === DocumentTemplateType.Task || docType === DocumentTemplateType.CaTask
              ? 'Resubmit'
              : 'Resubmit to Architect'}
          </MenuItem>
          <MenuItem value={RespondToReviewPropsResponseEnum.Resolved}>
            {docType === DocumentTemplateType.Task || docType === DocumentTemplateType.CaTask
              ? 'Accepted'
              : 'Resolved'}
          </MenuItem>
        </Select>
        {getContent()}
      </div>
      <div className={classes.actions}>
        {!isSubmitting ? (
          <Button
            variant="contained"
            disabled={getUploadDisabled()}
            onClick={handleSubmit}
            className={classes.submitButton}
          >
            Submit
          </Button>
        ) : (
          <div>
            <Typography style={{ fontWeight: 500, marginBottom: 8 }}>
              Updating document...
            </Typography>
            <CircularLoader />
          </div>
        )}
      </div>
      <FileUploadDialog
        open={uploadDialogOpen}
        handleClose={() => setUploadDialogOpen(false)}
        title={getUploadDialogTitle()}
        addFile={(f) => setUploadedFile(f as File)}
        addFiles={(files) => handleAddFiles(files as File[])}
        removeFile={() => setUploadedFile(undefined)}
        file={uploadedFile}
        newFiles={uploadedFiles}
        uploadMessage={uploadMessage}
        uploadProgress={uploadProgress}
        buttonType="submit"
        handleSubmit={uploadFiles}
        disableComments
        disableDesignUpload
      />
    </div>
  );
}
